import { useState } from "react";
import { CalcCard, NumField, ResultRow, PrimaryButton, SaveButton, Section } from "@/components/CalcUI";
import { useUnits, L } from "@/context/UnitsContext";
import { useUnitInputs, useOnUnitsChange } from "@/lib/units";
import { runCalc } from "@/lib/api";
import { toast } from "sonner";

export default function PressureControl() {
  const { units } = useUnits();
  const u = L(units);

  // Kill Mud Weight
  const [kmw, setKmw] = useUnitInputs(
    { sidpp: "500", current_mw: "10.0", tvd: "10000", safety_factor: "0.3" },
    { sidpp: "pressure", current_mw: "density", tvd: "length", safety_factor: "density" }
  );
  const [kmwOut, setKmwOut] = useState(null);

  // ICP / FCP
  const [icpIn, setIcpIn] = useUnitInputs(
    { sidpp: "500", scr: "900", current_mw: "10.0", kmw: "11.0" },
    { sidpp: "pressure", scr: "pressure", current_mw: "density", kmw: "density" }
  );
  const [icpOut, setIcpOut] = useState(null);
  const [fcpOut, setFcpOut] = useState(null);
  useOnUnitsChange(() => {
    setKmwOut(null);
    setIcpOut(null);
    setFcpOut(null);
  });

  // MAASP
  const [maaspIn, setMaaspIn] = useState({ lot_ppg: "14.5", current_mw: "10.0", shoe_tvd: "4500" });
  const [maaspOut, setMaaspOut] = useState(null);

  // Formation Pressure
  const [fpIn, setFpIn] = useState({ sidpp: "500", mw: "10.0", tvd: "10000" });
  const [fpOut, setFpOut] = useState(null);

  const parseN = (o) => Object.fromEntries(Object.entries(o).map(([k, v]) => [k, parseFloat(v)]));

  const calcKmw = async () => {
    try {
      const r = await runCalc("kill_mud_weight", parseN(kmw), units);
      setKmwOut(r);
    } catch (e) {
      toast.error("Invalid inputs");
    }
  };
  const calcIcp = async () => {
    try {
      const r1 = await runCalc("icp", parseN(icpIn));
      const r2 = await runCalc("fcp", parseN(icpIn));
      setIcpOut(r1);
      setFcpOut(r2);
    } catch (e) {
      toast.error("Invalid inputs");
    }
  };
  const calcMaasp = async () => {
    try {
      const r = await runCalc("maasp", parseN(maaspIn));
      setMaaspOut(r);
    } catch (e) {
      toast.error("Invalid inputs");
    }
  };
  const calcFp = async () => {
    try {
      const r = await runCalc("formation_pressure", parseN(fpIn));
      setFpOut(r);
    } catch (e) {
      toast.error("Invalid inputs");
    }
  };

  return (
    <div data-testid="pressure-control-page">
      <Section
        title="Pressure Control"
        subtitle="Well control formulas covering the Wait-and-Weight kill method, MAASP, and formation pressure evaluation. Use with current SIDPP/SICP and slow circulating rate data."
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <CalcCard
            testid="kmw-card"
            title="Kill Mud Weight"
            formula={units === "field" ? "KMW = SIDPP/(0.052·TVD) + MW + SF" : "KMW = SIDPP·1e5/(9.81·TVD)+MW+SF"}
          >
            <div className="grid grid-cols-2 gap-3">
              <NumField testid="kmw-sidpp" label="SIDPP" unit={u.pressure} value={kmw.sidpp} onChange={(v) => setKmw({ ...kmw, sidpp: v })} />
              <NumField testid="kmw-mw" label="Current MW" unit={u.density} value={kmw.current_mw} onChange={(v) => setKmw({ ...kmw, current_mw: v })} />
              <NumField testid="kmw-tvd" label="TVD" unit={u.length} value={kmw.tvd} onChange={(v) => setKmw({ ...kmw, tvd: v })} />
              <NumField testid="kmw-sf" label="Safety Factor" unit={u.density} value={kmw.safety_factor} onChange={(v) => setKmw({ ...kmw, safety_factor: v })} />
            </div>
            <div className="mt-4 flex justify-between items-center">
              <PrimaryButton testid="kmw-run" onClick={calcKmw}>Calculate</PrimaryButton>
              <SaveButton getPayload={() => kmwOut && { category: "Pressure Control", calculator: "kill_mud_weight", inputs: kmw, outputs: kmwOut, units, default_name: "Kill Mud Weight" }} />
            </div>
            {kmwOut && (
              <div className="mt-4 border-t border-[#334155] pt-3">
                <ResultRow label="Kill Mud Weight" value={kmwOut.kmw} unit={kmwOut.unit} emphasis />
              </div>
            )}
          </CalcCard>

          <CalcCard
            testid="icp-fcp-card"
            title="ICP / FCP"
            formula="ICP = SIDPP + SCR ; FCP = SCR·(KMW/OMW)"
          >
            <div className="grid grid-cols-2 gap-3">
              <NumField testid="icp-sidpp" label="SIDPP" unit={u.pressure} value={icpIn.sidpp} onChange={(v) => setIcpIn({ ...icpIn, sidpp: v })} />
              <NumField testid="icp-scr" label="SCR pressure" unit={u.pressure} value={icpIn.scr} onChange={(v) => setIcpIn({ ...icpIn, scr: v })} />
              <NumField testid="icp-omw" label="Current MW" unit={u.density} value={icpIn.current_mw} onChange={(v) => setIcpIn({ ...icpIn, current_mw: v })} />
              <NumField testid="icp-kmw" label="Kill MW" unit={u.density} value={icpIn.kmw} onChange={(v) => setIcpIn({ ...icpIn, kmw: v })} />
            </div>
            <div className="mt-4">
              <PrimaryButton testid="icp-run" onClick={calcIcp}>Calculate</PrimaryButton>
            </div>
            {(icpOut || fcpOut) && (
              <div className="mt-4 border-t border-[#334155] pt-3">
                <ResultRow label="ICP" value={icpOut?.icp} unit={u.pressure} emphasis />
                <ResultRow label="FCP" value={fcpOut?.fcp} unit={u.pressure} emphasis />
              </div>
            )}
          </CalcCard>

          <CalcCard
            testid="maasp-card"
            title="MAASP"
            formula="MAASP = (LOT − MW)·0.052·Shoe TVD"
            warning={maaspOut?.warning}
          >
            <div className="grid grid-cols-3 gap-3">
              <NumField testid="maasp-lot" label="LOT" unit="ppg" value={maaspIn.lot_ppg} onChange={(v) => setMaaspIn({ ...maaspIn, lot_ppg: v })} />
              <NumField testid="maasp-mw" label="Current MW" unit="ppg" value={maaspIn.current_mw} onChange={(v) => setMaaspIn({ ...maaspIn, current_mw: v })} />
              <NumField testid="maasp-shoe" label="Shoe TVD" unit="ft" value={maaspIn.shoe_tvd} onChange={(v) => setMaaspIn({ ...maaspIn, shoe_tvd: v })} />
            </div>
            <div className="mt-4">
              <PrimaryButton testid="maasp-run" onClick={calcMaasp}>Calculate</PrimaryButton>
            </div>
            {maaspOut && (
              <div className="mt-4 border-t border-[#334155] pt-3">
                <ResultRow label="MAASP" value={maaspOut.maasp} unit="psi" emphasis />
              </div>
            )}
          </CalcCard>

          <CalcCard
            testid="fp-card"
            title="Formation Pressure"
            formula="FP = SIDPP + MW·0.052·TVD"
          >
            <div className="grid grid-cols-3 gap-3">
              <NumField testid="fp-sidpp" label="SIDPP" unit="psi" value={fpIn.sidpp} onChange={(v) => setFpIn({ ...fpIn, sidpp: v })} />
              <NumField testid="fp-mw" label="MW" unit="ppg" value={fpIn.mw} onChange={(v) => setFpIn({ ...fpIn, mw: v })} />
              <NumField testid="fp-tvd" label="TVD" unit="ft" value={fpIn.tvd} onChange={(v) => setFpIn({ ...fpIn, tvd: v })} />
            </div>
            <div className="mt-4">
              <PrimaryButton testid="fp-run" onClick={calcFp}>Calculate</PrimaryButton>
            </div>
            {fpOut && (
              <div className="mt-4 border-t border-[#334155] pt-3">
                <ResultRow label="FP (pressure)" value={fpOut.fp_psi} unit="psi" emphasis />
                <ResultRow label="FP (equivalent MW)" value={fpOut.fp_ppg} unit="ppg" />
              </div>
            )}
          </CalcCard>
        </div>
      </Section>
    </div>
  );
}
